import { TimerMode } from '@/components/settings/timer-configuration/types'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

export enum ActivityState {
  NotStarted = 'not_started',
  InProgress = 'in_progress',
  Completed = 'completed',
  Paused = 'paused',
}
export interface RoundConfig {
  roundId?: number
  roundNumber?: number
  status?: ActivityState
  randrConsultation?: number
  advisorConsultation: number
  negotiation: number
  decisionSubmission: number
  isRandR: boolean
  autoReleaseEnabled?: boolean
  autoReleaseEventIds: number[]
  isRandrInfinite?: boolean
  isAdvisorInfinite?: boolean
  isNegotiationInfinite?: boolean
  isDecisionInfinite?: boolean
}

export interface TimerAlertsConfig {
  enableFiveMinuteVisual: boolean
  enableOneMinuteVisual: boolean
}

export interface TimerConfig {
  mode: TimerMode
  numberOfRounds: number
  roundConfigurations: RoundConfig[]
  alerts?: TimerAlertsConfig
}

export type PhaseType = 'randr' | 'advisor' | 'negotiation' | 'decision'

export interface PhaseStatus {
  id: number
  phaseType: PhaseType
  status: ActivityState
  duration: number
  startTime: string | null
  endTime: string | null
  isActive: boolean
  isInfinite?: boolean
}

export interface RoundStatus {
  id: number
  roundNumber: number
  status: ActivityState
  startTime: string | null
  endTime: string | null
}

export interface TimerStatus {
  mode: string
  currentRound: number
  totalRounds: number
  currentPhase: string
  timeRemaining: number
  canGoPreviousPhase?: boolean
  isRunning: boolean
  phaseData?: PhaseStatus
  phasesData?: PhaseStatus[]
  roundData?: RoundStatus
  /** Authoritative game-level GameRoomStatus int (1..4) from the backend timer
   *  payload. Preferred over nested round/phase statuses for paused detection —
   *  those can be absent or stale (e.g. a pause issued between phases). */
  gameStatus?: number
}

interface TimerConfigurationState {
  config: TimerConfig | null
  status: TimerStatus | null
  loading: boolean
  error: string | null
}

const initialState: TimerConfigurationState = {
  config: null,
  status: null,
  loading: false,
  error: null,
}

const timerConfigurationSlice = createSlice({
  name: 'timerConfiguration',
  initialState,
  reducers: {
    setTimerConfig: (state, action: PayloadAction<TimerConfig>) => {
      state.config = action.payload
      state.error = null
    },
    setTimerStatus: (state, action: PayloadAction<TimerStatus>) => {
      state.status = action.payload
      state.error = null
    },
    // Optimistic game-level status flip (pause/resume control results) without
    // replacing the rest of the timer status.
    setGameStatus: (state, action: PayloadAction<number>) => {
      if (state.status) {
        state.status.gameStatus = action.payload
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload
      state.loading = false
    },
    clearTimerConfiguration: (state) => {
      state.config = null
      state.status = null
      state.error = null
      state.loading = false
    },
  },
})

export const {
  setTimerConfig,
  setTimerStatus,
  setGameStatus,
  setLoading,
  setError,
  clearTimerConfiguration,
} = timerConfigurationSlice.actions

export default timerConfigurationSlice.reducer
